import React, { useState, useEffect, useContext, Suspense, lazy } from "react";
import { BrowserRouter, Route } from "react-router-dom";
import "./App.scss";
import Loading from "./components/loading/Loading";
import Header from "./components/header/Header";
import Footer from "./components/footer/Footer";
import MenuTab from "./components/menu/MenuTab";
import Login from "./components/login/Login";
import Home from "./components/home/Home";
import CwLogin from "./components/login/CwLogin";

import MemberContext from "./store/Member";
import UnEventContext from "./store/UnEvent";
import CommonContext from "./store/Common";
import MobilePopupContext from "./store/MobilePopup";

import API from "./util/api";
import UnEventAPI from "./API/unevent";
import AdminAPI from "./API/admin";
import MemberAPI from "./API/member";
import CommonAPI from "./API/common";
import { resolveWorkingOrganizationType } from "./util/resolveWorkingOrganizationType";

import { CacheRoute } from "react-router-cache-route";
import { ToastContainer } from "react-toastify";

import RouteIf from "./routes/common/RouteIf";
import UserAuthRoute from "./routes/common/UserAuthRoute";
import ScrollToTop from "./util/ScrollToTop";
import ScrollTopButton from "./components/common/ScrollTopButton";
import { bindEnterToSearch } from "./util/bindEnterToSearch";
import withAgentManagedCompany from "./routes/common/withAgentManagedCompany";
import withDenyInventor from "./routes/common/withDenyInventor";
import withRequireAdmin from "./routes/common/withRequireAdmin";
import CustomSwitch from "./routes/common/CustomSwitch";
import config from "./const/config";
import links from "./const/links";
import qs from "query-string";

// 초기 진입에 필요한 것만 eager — 나머지는 라우트 단위로 청크 분할
const CompanyChoice = lazy(() => import("./components/choice/CompanyChoice"));
const UnEventView = lazy(() => import("./components/unEvent/UnEventView"));
const SignupInvite = lazy(() => import("./components/signup/SignupInvite"));
const UserChatAdmin = lazy(() => import("./components/chatbot/UserChatAdmin"));
const ItlCompany = lazy(() => import("./components/itl/ItlCompany"));
const IntroContainer = lazy(() => import("./components/main/IntroContainer"));
const MailRefuse = lazy(() => import("./components/mailRefuse/MailRefuse"));
const GradeCompany = lazy(() => import("./components/grade/GradeCompany"));
const AdminTaskPosting = lazy(() =>
  import("./components/admin/AdminTaskPosting")
);
const AdminTaskContentList = lazy(() =>
  import("./components/admin/AdminTaskContentList")
);
const People = lazy(() => import("./components/people/People"));
const CyberMenuBar = lazy(() => import("./components/cyber/CyberMenuBar"));
const CyberHeader = lazy(() => import("./components/cyber/CyberHeader"));
const BusinessDescriptionMain = lazy(() =>
  import("./components/businessDescription/BusinessDescriptionMain")
);
const LawfirmMain = lazy(() => import("./components/lawfirm/LawfirmMain"));
const Calculator = lazy(() => import("./components/calc/Calculator"));
const AlbaMailing = lazy(() => import("./components/admin/albal/AlbaMailing"));
const AdminMarketSurvey = lazy(() =>
  import("./components/admin/manage/AdminMarketSurvey")
);
const AdminMarketList = lazy(() =>
  import("./components/admin/manage/AdminMarketList")
);
const SideBanner = lazy(() => import("./components/common/SideBanner"));
const PlanInfo = lazy(() => import("./components/businessPlan/PlanInfo"));
const PaymentHistory = lazy(() =>
  import("./components/statistic/PaymentHistory")
);
const TaskDownload = lazy(() =>
  import("./components/dataVoucher/TaskDownload")
);
const AllDownload = lazy(() => import("./components/dataVoucher/AllDownload"));
const ChartTest = lazy(() => import("./components/chart/ChartTest"));
const PatentDownload = lazy(() =>
  import("./components/dataVoucher/PatentDownload")
);
const IncriselPopupLog = lazy(() =>
  import("./components/popup/IncriselPopupLog")
);
const DummyLogin = lazy(() => import("./components/login/DummyLogin"));
const AnnualFeeLogin = lazy(() => import("./components/login/AnnualFeeLogin"));
const TestTaskFina = lazy(() => import("./components/test/task/fina"));
const TestResearchDownload = lazy(() =>
  import("./components/test/research/download")
);
const TrademarkSearch = lazy(() =>
  import("./components/test/company/trademark")
);
const TestEsgMediaNewsList = lazy(() => import("./components/test/esg/news"));
const IpStatistic = lazy(() => import("./components/statistic/IpStatistic"));
const IpStatisticPage = lazy(() =>
  import("./components/statistic/IpStatisticPage")
);
const DDashboard = lazy(() => import("./components/dueDiligence/DDashboard"));
const TechTransferMain = lazy(() =>
  import("./components/techTransfer/TechTransferMain")
);
const TechTransferSimple = lazy(() =>
  import("./components/techTransfer/simple/TechTransferSimple")
);
const HistoryAlarmView = lazy(() =>
  import("./components/unEvent/HistoryAlarmView")
);
const AlarmView = lazy(() => import("./components/unEvent/AlarmView"));

const ApprovalRoute = lazy(() => import("./routes/ApprovalRoute"));
const AdminRoute = lazy(() => import("./routes/AdminRoute"));
const SignupRoute = lazy(() => import("./routes/SignupRoute"));
const PatentRoute = lazy(() => import("./routes/PatentRoute"));
const ManageRoute = lazy(() => import("./routes/ManageRoute"));
const IntroRoute = lazy(() => import("./routes/IntroRoute"));
const MarketInfoRoute = lazy(() => import("./routes/MarketInfoRoute"));
const DesignRoute = lazy(() => import("./routes/DesignRoute"));
const AnnualRoute = lazy(() => import("./routes/AnnualRoute"));
const AnnualCountRoute = lazy(() => import("./routes/AnnualCountRoute"));
const TradeMarkRoute = lazy(() => import("./routes/TradeMarkRoute"));
const InventionRoute = lazy(() => import("./routes/InventionRoute"));
const DeadlineRoute = lazy(() => import("./routes/DeadlineRoute"));
const CostRoute = lazy(() => import("./routes/CostRoute"));
const ApplicationRoute = lazy(() => import("./routes/ApplicationRoute"));
const PatentIntroRoute = lazy(() => import("./routes/PatentIntroRoute"));
const MyPageRoute = lazy(() => import("./routes/MyPageRoute"));
const DocTradeRoute = lazy(() => import("./routes/DocTradeRoute"));
const ConsortiumRoute = lazy(() => import("./routes/ConsortiumRoute"));
const ExpositionRoute = lazy(() => import("./routes/ExpositionRoute"));
const GovSupportRoute = lazy(() => import("./routes/GovSupportRoute"));
const MatchingBizRoute = lazy(() => import("./routes/MatchingBizRoute"));
const ConsultComRoute = lazy(() => import("./routes/ConsultComRoute"));
const CyberRoute = lazy(() => import("./routes/CyberRoute"));
const TradeRoute = lazy(() => import("./routes/TradeRoute"));
const TaskManageRoute = lazy(() => import("./routes/TaskManageRoute"));
const FreeApplyRoute = lazy(() => import("./routes/FreeApplyRoute"));
const EducationRoute = lazy(() => import("./routes/EducationRoute"));
const TutorialRoute = lazy(() => import("./routes/TutorialRoute"));
const TermsRoute = lazy(() => import("./routes/TermsRoute"));
const FindRoute = lazy(() => import("./routes/FindRoute"));
const NotifyRoute = lazy(() => import("./routes/NotifyRoute"));
const CommunityRoute = lazy(() => import("./routes/CommunityRoute"));
const BoardRoute = lazy(() => import("./routes/BoardRoute"));
const MobileRoute = lazy(() => import("./routes/MobileRoute"));
const ResearchTechRoute = lazy(() => import("./routes/ResearchTechRoute"));
const BusinessPlanRoute = lazy(() => import("./routes/BusinessPlanRoute"));
const MailingRoute = lazy(() => import("./routes/MailingRoute"));

const PatentRouteGuarded = withAgentManagedCompany(PatentRoute);
const DesignRouteGuarded = withAgentManagedCompany(DesignRoute);
const TradeMarkRouteGuarded = withAgentManagedCompany(TradeMarkRoute);
const CostRouteGuarded = withDenyInventor(withAgentManagedCompany(CostRoute));
const InventionRouteGuarded = withAgentManagedCompany(InventionRoute);
const AnnualRouteGuarded = withDenyInventor(AnnualRoute);
const AnnualCountRouteGuarded = withDenyInventor(AnnualCountRoute);
const DeadlineRouteGuarded = withDenyInventor(DeadlineRoute);
const AdminRouteGuarded = withRequireAdmin(AdminRoute);

function App() {
  const token = window.$Global.getToken();
  const auth = token != null;
  const [unEvent, setUnEvent] = useState({});
  const [member, setMember] = useState({});
  const [loading, setLoading] = useState(false);
  const [mobilePopup, setMobilePopup] = useState(
    useContext(MobilePopupContext)
  );
  const { isMobile } = useContext(CommonContext);
  const [common, setCommon] = useState(useContext(CommonContext));
  const errorMsg = "Error가 발생하였습니다.\n잠시 후 이용해주세요";
  const isIE = /*@cc_on!@*/ !!document.documentMode;
  const isCyberPath = window.location.href.indexOf("/cyber") > -1;
  const isLoginPath = window.location.href.indexOf("/login") > -1;
  const queryData = qs.parse(window.location.search);
  const isTestPath =
    window.location.href.indexOf("/test") > -1 ||
    window.location.href.indexOf("/sendMail") > -1;

  if (queryData.token !== undefined) {
    MemberAPI.loginToken({ token: queryData.token }).then((res) => {
      const data = res.data;

      if (data.token) {
        sessionStorage.setItem(config.keyToken, data.token);

        window.location.href = "/";
      }
    });
  }

  useEffect(() => {
    if (queryData.code) {
      sessionStorage.setItem("code", queryData.code);
    }
  }, [queryData.code]);

  useEffect(() => bindEnterToSearch(), []);

  useEffect(() => {
    if (isIE) {
      alert(
        "본 서비스는 익스플로러 환경을 지원하지 않습니다\n원활한 사용을 위해 크롬, 엣지로 이용해주세요"
      );
    }

    let redirectTimer;
    let count = 0;
    let lastUnhandledAlert = { message: "", at: 0 };

    const shouldSkipGlobalLoading = (reqConfig) => {
      if (reqConfig?.skipGlobalLoading) return true;
      const path = window.location.pathname || "";
      // 홈/대시보드 등 기존 제외 + 리스트 자체 overlay 사용 페이지(전역 스피너 중복 방지)
      if (
        path === "/dueDi/dashboard" ||
        path === "/" ||
        path === "/tech/transfer"
      ) {
        return true;
      }
      if (
        /\/(patent|design|tradeMark|invention)\/list$/.test(path) ||
        /\/deadline\/list$/.test(path) ||
        /\/annual\/\d+\/list$/.test(path)
      ) {
        return true;
      }
      return false;
    };

    const reqId = API.interceptors.request.use(
      (config) => {
        if (shouldSkipGlobalLoading(config)) {
          // 리스트 overlay 등과 전역 스피너가 겹치지 않도록
          setLoading(false);
        } else {
          setLoading(true);
        }

        count += 1;

        return config;
      },
      (error) => Promise.reject(error)
    );

    // HTTP 에러 alert는 하지 않음(화면 catch와 중복 방지).
    // catch 없는 요청은 아래 unhandledrejection에서만 alert.
    const resId = API.interceptors.response.use(
      (response) => {
        count -= 1;

        if (count == 0) {
          setLoading(false);
        }

        return response;
      },
      (error) => {
        count -= 1;
        setLoading(false);

        if (error.response) {
          const data = error.response.data || {};
          if (!redirectTimer) {
            redirectTimer = setTimeout(() => {
              redirectTimer = null;
              let code = data.status;
              if (code == 401 || code == 603 || code == 604) {
                window.$Global.saveHistory();
                sessionStorage.removeItem("idx");
                sessionStorage.removeItem(config.keyToken);
                window.location.href = "/login";
              }

              if (code == 607 || code == 608) {
                window.location.href = "/mypage/info";
              }

              if (data.code == "not_exist_company") {
                sessionStorage.removeItem("manage_company_idx");
                window.location.href = "/choice";
              }
              if (code == 600) {
                window.location.href = "/";
              }

              if (code == 601) {
                window.location.href = "/manage/price";
              }
              // 403/권한 없음 리다이렉트는 unhandledrejection 에서만 처리
              // (화면 catch + alert 와 history.back 중복 방지)
            }, 0);
          }
        } else if (error.request) {
          console.error(error.request);
        }

        return Promise.reject(error);
      }
    );

    const isForbiddenError = (reason) => {
      const data = reason?.response?.data || {};
      const httpStatus = reason?.response?.status;
      return (
        httpStatus === 403 ||
        data.status == 403 ||
        data.code === "forbidden" ||
        data.message === "권한 없음"
      );
    };

    const goBackOnForbidden = () => {
      if (window.history.length > 1) {
        window.history.back();
      } else {
        window.location.href = "/";
      }
    };

    const onUnhandledRejection = (event) => {
      const reason = event?.reason;
      if (!reason) return;

      const isAxiosLike =
        reason.isAxiosError ||
        reason.response != null ||
        reason.request != null;
      if (!isAxiosLike) return;

      // CRA 개발용 빨간 "Uncaught runtime errors" 오버레이가 뜨지 않도록 기본 처리 막기
      if (typeof event.preventDefault === "function") {
        event.preventDefault();
      }

      if (reason.config?.skipGlobalErrorAlert) return;

      const data = reason.response?.data;
      const message =
        (data && (data.message || data.msg)) ||
        (typeof data === "string" && data.trim() ? data : null) ||
        (reason.response?.status
          ? `요청 처리에 실패했습니다. (${reason.response.status})`
          : null) ||
        errorMsg;

      const now = Date.now();
      if (
        message === lastUnhandledAlert.message &&
        now - lastUnhandledAlert.at < 300
      ) {
        return;
      }
      lastUnhandledAlert = { message, at: now };
      alert(message);

      // catch 없는 요청의 권한 없음 → alert 후 이전 페이지
      if (isForbiddenError(reason) && !reason.config?.skipGlobalForbiddenRedirect) {
        goBackOnForbidden();
      }
    };

    window.addEventListener("unhandledrejection", onUnhandledRejection);

    return () => {
      API.interceptors.request.eject(reqId);
      API.interceptors.response.eject(resId);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
      if (redirectTimer) {
        clearTimeout(redirectTimer);
        redirectTimer = null;
      }
    };
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem("idx")) {
      let initData = {};

      for (let i = 0; i < sessionStorage.length; i++) {
        let key = sessionStorage.key(i);

        if (key == "manage_company_idx") {
          initData[key] = sessionStorage.getItem(key) || null;
        } else if (key == "admin") {
          initData[key] = Boolean(
            sessionStorage.getItem(key) == "true" ? 1 : 0
          );
        } else {
          initData[key] = sessionStorage.getItem(key);
        }
      }
      setMember(initData);
    } else if (token) {
      MemberAPI.getInfo().then(async (res) => {
        let data = res.data;
        data.auth_name = window.$Global.convertAuthCodeToStr(res.data.rank);
        const isAdmin = await AdminAPI.checkAuth().then((res) => res.data);
        const initData = {
          ...data,
          auth: data.rank,
          manage_company_idx:
            sessionStorage.getItem("manage_company_idx") || null,
          admin: isAdmin,
        };
        sessionStorage.setItem("idx", data.idx);
        sessionStorage.setItem("id", data.id);
        sessionStorage.setItem("name", data.name);
        sessionStorage.setItem("email", data.email);
        sessionStorage.setItem("phone", data.phone);
        sessionStorage.setItem("birth_at", data.birth_at);
        sessionStorage.setItem("auth", data.rank);
        sessionStorage.setItem("rank", data.rank);
        sessionStorage.setItem("company_name", data.company_name);
        sessionStorage.setItem("company_idx", data.company_idx);
        sessionStorage.setItem("auth_name", data.auth_name);
        sessionStorage.setItem("admin", isAdmin);
        sessionStorage.setItem("ipnow_permission", data.ipnow_permission);
        sessionStorage.setItem("company_type", data.company_type);
        if (data.organization_type != null) {
          sessionStorage.setItem(
            "organization_type",
            String(data.organization_type)
          );
        }
        sessionStorage.setItem("inventor_consent", data.inventor_consent);

        let keys = Object.keys(initData);

        keys.forEach((item) => {
          if (item == "admin") {
            sessionStorage.setItem("admin", isAdmin);
          } else if (initData[item] != null) {
            sessionStorage.setItem(item, initData[item]);
          }
        });
        setMember(initData);
      });
    }
  }, []);

  useEffect(() => {
    const applyServiceFlags = (res) => {
      const statisticShow = res.data.statistics_service === 1;
      const simpleStatisticShow = res.data.simple_statistics_service === 1;
      const transfer_statisticShow =
        res.data.transfer_statistics_service === 1;
      // 기본 표시(1). 명시적 0일 때만 숨김 (행 없음/미정의도 표시)
      const annualPayShow = Number(res.data?.annual_pay_service) !== 0;
      // 게시판: 명시적 1일 때만 표시 (미설정/0은 숨김)
      const boardShow = Number(res.data?.board_service) === 1;
      const organizationType =
        res.data?.organization_type != null
          ? String(res.data.organization_type)
          : "";
      sessionStorage.setItem("statistics_service", statisticShow);
      sessionStorage.setItem(
        "simple_statistics_service",
        simpleStatisticShow
      );
      sessionStorage.setItem(
        "transfer_statistics_service",
        transfer_statisticShow
      );
      sessionStorage.setItem("annual_pay_service", annualPayShow);
      sessionStorage.setItem("board_service", boardShow);
      if (organizationType !== "") {
        sessionStorage.setItem("organization_type", organizationType);
      }
      setCommon((prev) => {
        const nextType = organizationType || prev.organization_type;
        if (
          prev.statistics_service === statisticShow &&
          prev.simple_statistics_service === simpleStatisticShow &&
          prev.transfer_statistics_service === transfer_statisticShow &&
          prev.annual_pay_service === annualPayShow &&
          prev.board_service === boardShow &&
          prev.organization_type === nextType
        ) {
          return prev;
        }
        return {
          ...prev,
          statistics_service: statisticShow,
          simple_statistics_service: simpleStatisticShow,
          transfer_statistics_service: transfer_statisticShow,
          annual_pay_service: annualPayShow,
          board_service: boardShow,
          organization_type: nextType,
        };
      });
    };

    const refreshServiceFlags = () => {
      CommonAPI.getAnnualPayUsableCheck()
        .then(applyServiceFlags)
        .catch(() => {});

      // 특사: 선택 기업(manage_company_idx) 기준 organization_type
      resolveWorkingOrganizationType({
        auth: member.auth ?? member.rank,
        member,
      }).then((orgType) => {
        if (orgType == null) return;
        const nextType = String(orgType);
        sessionStorage.setItem("organization_type", nextType);
        setCommon((prev) =>
          prev.organization_type === nextType
            ? prev
            : { ...prev, organization_type: nextType }
        );
      });
    };

    if (member.idx) {
      const updateUnEvent = (newData) => {
        setUnEvent(prev => ({
          ...prev,
          ...newData,
          setUnEvent: updateUnEvent
        }));
      };
      
      Promise.all([
        // UnEventAPI.getNotification(member.manage_company_idx),
        UnEventAPI.getNotificationUnreadCount(),
        UnEventAPI.getNotificationHistoryUnreadCount()
      ]).then(([notificationRes, historyRes]) => {
        setUnEvent({
          // unConfirmEventCnt: notificationRes.data.filter(
          //   (item) => item.state == 0
          // ).length,
          unConfirmEventCnt: notificationRes.data.unread_count || 0,
          historyAlarmCount: historyRes.data.unread_count || 0,
          showHistoryAlarm: historyRes.data.showHistoryAlarm || false,
          setUnEvent: updateUnEvent
        });
      });
      refreshServiceFlags();
    }

    window.addEventListener("ipnow:refresh-service-flags", refreshServiceFlags);
    return () => {
      window.removeEventListener(
        "ipnow:refresh-service-flags",
        refreshServiceFlags
      );
    };
  }, [member]);

  useEffect(() => {
    handleResize(window.$Global.getBrowserSize().width < 850);

    window.addEventListener("resize", () =>
      handleResize(window.$Global.getBrowserSize().width < 850)
    );
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleResize = (result) => {
    setCommon((prev) => ({ ...prev, isMobile: result }));
  };

  const path = {
    intro: "/intro",
    terms: "/terms",
    find: "/find",
    qna: "/qna",
    notify: "/notify",
    nowLove: "/nowLove",
    signup: "/signup",
    admin: "/admin",
    annual: "/annual",
    annualCount: "/annualCount",
    tradeMark: "/tradeMark",
    invention: "/invention",
    deadline: "/deadline",
    cost: "/cost",
    application: "/application",
    patent: "/patent",
    patentIntro: "/patentIntro",
    myPage: "/myPage",
    grade: "/grade",
    design: "/design",
    tutorial: "/tutorial",
    education: "/education",
    freeApply: "/freeApply",
    trade: "/trade",
    taskManage: "/taskManage",
    manage: "/manage",
    people: "/people",
    community: "/community",
    board: "/board",
    cyber: "/cyber",
    docTrade: "/docTrade",
    userChat: "/userChat",
    consortium: "/consortium",
    matchingBiz: "/matchingBiz",
    govSupport: "/govSupport",
    exposition: "/exposition",
    businessDescription: "/businessDescription",
    taskDetail: "/taskDetail",
    lawFirm: "/lawfirm",
    graph: "/graph",
    consultCom: "/consultCom",
    calc: "/calc",
    marketInfo: "/marketInfo",
    researchTech: "/researchTech",
    approval: "/approval",
    mobile: "/mobile",
    mailing: "/sendMail",
    mailRefuse: "/mailRefuse",
    plan: "/plan",
    dueDi: "/dueDi",
    main: "/main",
  };

  if (window.location.href.indexOf("/login") == -1) {
    sessionStorage.removeItem("historyURL");
  }

  if (auth && window.$Global.isEmptyObject(member)) return null;

  return (
    <MobilePopupContext.Provider value={{ mobilePopup, setMobilePopup }}>
      <CommonContext.Provider value={common}>
        <UnEventContext.Provider value={unEvent}>
          <MemberContext.Provider value={member}>
            <Loading show={loading} />
            <ToastContainer
              position="top-center"
              autoClose={2200}
              hideProgressBar
              newestOnTop
              closeOnClick
              pauseOnHover
              draggable={false}
              limit={3}
            />
            <BrowserRouter forceRefresh={false}>
              <ScrollToTop />
              <ScrollTopButton />
              <Suspense fallback={<Loading show />}>
              <div
                id="App"
                className="App"
                style={{ pointerEvents: loading ? "none" : "" }}
              >
                <CustomSwitch>
                  <Route
                    path="/lti_admin/company"
                    component={ItlCompany}
                    exact
                  />
                  <UserAuthRoute
                    exact
                    path={`${path.admin}/posting/task`}
                    auth={true}
                    component={AdminTaskPosting}
                  />
                  <UserAuthRoute
                    exact
                    path={`${path.admin}/AlbaMailing`}
                    auth={true}
                    component={AlbaMailing}
                  />

                  <UserAuthRoute
                    exact
                    path={`${path.admin}/AlbaMarket/list`}
                    auth={true}
                    component={AdminMarketList}
                  />
                  <UserAuthRoute
                    exact
                    path={`${path.admin}/AlbaMarket/posting`}
                    auth={true}
                    component={AdminMarketSurvey}
                  />

                  <CacheRoute
                    exact
                    path={`${path.admin}/posting/task/list`}
                    component={AdminTaskContentList}
                    when="always"
                  />

                  <Route
                    exact
                    path="/statistic/paymentHistory"
                    component={PaymentHistory}
                  />

                  <Route exact path="/chart/test" component={ChartTest} />

                  <Route
                    exact
                    path="/test/task/fina"
                    component={TestTaskFina}
                  />

                  <Route
                    exact
                    path="/test/research/download"
                    component={TestResearchDownload}
                  />

                  <Route
                    exact
                    path="/test/company/trademark"
                    component={TrademarkSearch}
                  />

                  <Route
                    exact
                    path="/test/esg/news"
                    component={TestEsgMediaNewsList}
                  />

                  <Route
                    exact
                    path="/dataVoucher/task"
                    component={TaskDownload}
                  />
                  <Route
                    exact
                    path="/dataVoucher/download"
                    component={AllDownload}
                  />
                  <Route
                    exact
                    path="/dataVoucher/patent"
                    component={PatentDownload}
                  />
                  <Route
                    exact
                    path="/visitCount/incrisel"
                    component={IncriselPopupLog}
                  />
                  <React.Fragment>
                    {!isCyberPath && (
                      <>
                        {!isTestPath &&
                          !(isLoginPath && (queryData.code == "HJESB7" || sessionStorage.getItem("code") == "HJESB7")) && (
                            <Route path="*" component={Header} />
                          )}
                        {!isTestPath &&
                          !(isLoginPath && (queryData.code == "HJESB7" || sessionStorage.getItem("code") == "HJESB7")) && (
                            <Route path="*" component={MenuTab} />
                          )}
                        <Route
                          exact
                          path={["/intro/main", "/"]}
                          component={SideBanner}
                        />
                        <Route exact path="/plan/*" component={PlanInfo} />
                      </>
                    )}
                    <Route path={`${path.cyber}`} component={CyberHeader} />

                    <div id="container" className="container">
                      {/*<Route exact path={`${path.intro}/*`} component={DemoView}/>*/}
                      <CustomSwitch>
                      <Route
                          exact
                          path="/"
                          render={(props) => {
                            if (auth) {
                              return <Home {...props} />;
                            }

                            window.location.replace("/intro/main");
                            return null;
                          }}
                        />
                        <RouteIf
                          exact
                          path="/main"
                          condition={!auth}
                          redirectPath="/main"
                          component={IntroContainer}
                        />
                        <RouteIf
                          exact
                          path="/login"
                          condition={!auth}
                          redirectPath="/"
                          component={
                            queryData.code == "HJESB7" || sessionStorage.getItem("code") == "HJESB7" 
                              ? CwLogin 
                              : Login
                          }
                        />
                        <RouteIf
                          exact
                          path="/dummylogin"
                          condition={!auth}
                          redirectPath="/"
                          component={DummyLogin}
                        />
                         <RouteIf
                          exact
                          path={path.main}
                          condition={!auth}
                          redirectPath="/"
                          component={Home}
                        />
                        <RouteIf
                          exact
                          path="/annual/login"
                          condition={!auth}
                          redirectPath="/"
                          component={AnnualFeeLogin}
                        />
                        <UserAuthRoute
                          exact
                          path="/choice"
                          auth={auth}
                          component={CompanyChoice}
                        />
                        <UserAuthRoute
                          exact
                          path="/unEvent"
                          auth={auth}
                          component={UnEventView}
                        />
                         <UserAuthRoute
                          exact
                          path="/alarm"
                          auth={auth}
                          component={AlarmView}
                        />
                        <UserAuthRoute
                          exact
                          path="/history/alarm"
                          auth={auth}
                          component={HistoryAlarmView}
                        />
                        <Route exact path="/invite" component={SignupInvite} />
                        <Route exact path="/people" component={People} />
                        <Route exact path={path.calc} component={Calculator} />
                        <Route
                          exact
                          path={`${path.grade}/company`}
                          component={GradeCompany}
                        />
                        <UserAuthRoute
                          exact
                          path={`${path.userChat}/admin`}
                          requireAuth={true}
                          component={UserChatAdmin}
                        />
                        <UserAuthRoute
                          exact
                          path={`${path.businessDescription}/list`}
                          requireAuth={false}
                          component={BusinessDescriptionMain}
                        />
                        <UserAuthRoute
                          exact
                          path={`${path.lawFirm}/list`}
                          requireAuth={false}
                          component={LawfirmMain}
                        />

                        <Route path={path.cyber} component={CyberRoute} />
                        <Route path={path.trade} component={TradeRoute} />
                        <Route
                          path={path.taskManage}
                          component={TaskManageRoute}
                        />
                        <Route
                          path={path.freeApply}
                          component={FreeApplyRoute}
                        />
                        <Route
                          path={path.education}
                          component={EducationRoute}
                        />
                        <Route path={path.tutorial} component={TutorialRoute} />
                        <Route path={path.manage} component={ManageRoute} />
                        <Route path={path.intro} component={IntroRoute} />
                        <Route path={path.terms} component={TermsRoute} />
                        <Route path={path.find} component={FindRoute} />
                        <Route path={path.notify} component={NotifyRoute} />
                        <Route
                          path={path.community}
                          component={CommunityRoute}
                        />
                        <UserAuthRoute
                          path={path.board}
                          requireAuth={true}
                          component={BoardRoute}
                        />
                        <Route path={path.signup} component={SignupRoute} />
                        <Route path={path.admin} component={AdminRouteGuarded} />
                        <Route path={path.patent} component={PatentRouteGuarded} />
                        <Route path={path.design} component={DesignRouteGuarded} />
                        <Route path={path.annual} component={AnnualRouteGuarded} />
                        <Route
                          path={path.annualCount}
                          component={AnnualCountRouteGuarded}
                        />
                        <Route
                          path={path.tradeMark}
                          component={TradeMarkRouteGuarded}
                        />
                        <Route
                          path={path.invention}
                          component={InventionRouteGuarded}
                        />
                        <Route path={path.deadline} component={DeadlineRouteGuarded} />
                        <Route path={path.cost} component={CostRouteGuarded} />
                        <Route path={path.approval} component={ApprovalRoute} />
                        <Route
                          path={path.application}
                          component={ApplicationRoute}
                        />
                        <Route
                          path={path.patentIntro}
                          component={PatentIntroRoute}
                        />
                        <Route path={path.myPage} component={MyPageRoute} />
                        <Route path={path.docTrade} component={DocTradeRoute} />
                        <Route
                          path={path.consortium}
                          component={ConsortiumRoute}
                        />
                        <Route
                          path={path.exposition}
                          component={ExpositionRoute}
                        />
                        <Route
                          path={path.govSupport}
                          component={GovSupportRoute}
                        />
                        <Route
                          path={path.matchingBiz}
                          component={MatchingBizRoute}
                        />
                        <Route
                          path={path.consultCom}
                          component={ConsultComRoute}
                        />
                        <Route
                          path={path.marketInfo}
                          component={MarketInfoRoute}
                        />
                        <Route
                          path={path.researchTech}
                          component={ResearchTechRoute}
                        />
                        <Route
                          path={links.mobile.path}
                          component={MobileRoute}
                        />
                        <Route path={path.mailing} component={MailingRoute} />
                        <Route path={path.plan} component={BusinessPlanRoute} />
                        <RouteIf
                          exact
                          path={path.mailRefuse}
                          condition={!auth}
                          redirectPath="/"
                          component={MailRefuse}
                        />
                        <UserAuthRoute
                          exact
                          path={`/kautum/statistic`}
                          requireAuth={true}
                          component={IpStatistic}
                        />
                        <UserAuthRoute
                          exact
                          path={`/tech/transfer/simple`}
                          requireAuth={true}
                          component={TechTransferSimple}
                        />
                        <UserAuthRoute
                          exact
                          path={`/tech/transfer`}
                          requireAuth={true}
                          component={TechTransferMain}
                        />
                        <UserAuthRoute
                          exact
                          path={`/statistic/ip`}
                          requireAuth={true}
                          component={IpStatisticPage}
                        />
                        <UserAuthRoute
                          path={path.dueDi}
                          requireAuth={true}
                          component={DDashboard}
                        />
                      </CustomSwitch>
                    </div>
                    {!isCyberPath && (
                      !isTestPath &&
                      !(isLoginPath && (queryData.code == "HJESB7" || sessionStorage.getItem("code") == "HJESB7")) && (
                        <Route path="*" component={Footer} />
                      )
                    )}
                    <Route path={`${path.cyber}`} component={CyberMenuBar} />
                  </React.Fragment>
                </CustomSwitch>
              </div>
              </Suspense>
            </BrowserRouter>
          </MemberContext.Provider>
        </UnEventContext.Provider>
      </CommonContext.Provider>
    </MobilePopupContext.Provider>
  );
}

export default App;
